<template>
  <div>
    <!-- Stats Cards -->
    <a-row type="flex" align="top" :gutter="[16, 16]" style="margin-top: 30px">
      <a-col :span="6" v-for="stat in stats" :key="stat.title">
        <div
          style="
            background: #fff;
            border-radius: 6px;
            padding: 20px;
            border: 1px solid #e6e6e6;
          "
        >
          <a-statistic
            :value-style="{
              color: '#05004E',
              fontSize: '16px',
              fontWeight: 'bold',
            }"
            :title-style="{ fontSize: '16px' }"
            :value="stat.value"
          >
            <template #title>
              <div
                style="
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                "
              >
                <span>{{ stat.title }}</span>
                <i :class="stat.icon" :style="stat.iconStyle"></i>
              </div>
            </template>
          </a-statistic>
        </div>
      </a-col>
    </a-row>

    <!-- Filters & Search -->
    <div style="margin-top: 40px">
      <a-row :gutter="16">
        <a-col :span="6">
          <p style="margin: 0px">Search</p>
          <a-input
            v-model="search"
            placeholder="Search by Company Name..."
            style="width: 100%"
            allow-clear
            prefix-icon="search"
          />
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Status</p>
          <a-select
            v-model="filters.accountStatus"
            placeholder="Filter by Status"
            allowClear
            style="width: 100%"
          >
            <a-select-option
              v-for="status in statusOptions"
              :key="status"
              :value="status"
              >{{ status }}</a-select-option
            >
          </a-select>
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Segment Type</p>
          <a-select
            v-model="filters.segmentType"
            placeholder="Filter by Segment Type"
            allowClear
            style="width: 100%"
          >
            <a-select-option
              v-for="type in segmentTypeOptions"
              :key="type"
              :value="type"
              >{{ type }}</a-select-option
            >
          </a-select>
        </a-col>
        <a-col :span="6">
          <p style="margin: 0px">Payment Terms</p>
          <a-select
            v-model="filters.paymentTerms"
            placeholder="Filter by Payment Terms"
            allowClear
            style="width: 100%"
          >
            <a-select-option
              v-for="term in paymentTermsOptions"
              :key="term.value"
              :value="term.value"
              >{{ term.label }}</a-select-option
            >
          </a-select>
        </a-col>
      </a-row>
    </div>

    <!-- Key Accounts Table -->
    <div style="margin-top: 40px">
      <a-table
        :columns="keyAccountColumns"
        :data-source="keyAccountData"
        rowKey="companyName"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :loading="keyAccountLoading"
        @change="handleTableChange"
        style="background-color: #fafafa"
      >
        <template slot="actions" slot-scope="text, record">
          <a-button
            type="link"
            icon="eye"
            style="cursor: pointer; color: #26a246"
            @click="() => viewKeyAccount(record)"
          />
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import sellersService from "../../../api/sellers.service";
import { formatAmount } from "../../../utils";

export default {
  name: "KeyAccountList",
  props: {
    sellerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      search: "",
      filters: {
        accountStatus: undefined,
        segmentType: undefined,
        paymentTerms: undefined,
      },
      searchDebounceTimer: null,
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },
      keyAccountColumns: [
        { title: "Company Name", dataIndex: "companyName", key: "companyName" },
        { title: "Segment Type", dataIndex: "segmentType", key: "segmentType" },
        {
          title: "Account Manager",
          dataIndex: "accountManager",
          key: "accountManager",
        },
        {
          title: "Primary Contact",
          dataIndex: "primaryContact",
          key: "primaryContact",
          customRender: (text, record) => {
            if (!record.primaryContact) return "N/A";
            return `${record.primaryContact.firstName} ${record.primaryContact.lastName}`;
          },
        },
        {
          title: "Contact Info",
          dataIndex: "contactInfo",
          key: "contactInfo",
          customRender: (text, record) => {
            if (!record.primaryContact) return "N/A";
            return `${record.primaryContact.phone}`;
          },
        },
        {
          title: "Location",
          dataIndex: "location",
          key: "location",
          customRender: (text, record) => {
            if (!record.address) return "N/A";
            return `${record.address.city}, ${record.address.region}`;
          },
        },
        {
          title: "Purchase Value",
          dataIndex: "purchaseValue",
          key: "purchaseValue",
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: "Expected Revenue",
          dataIndex: "expectedRevenue",
          key: "expectedRevenue",
          customRender: (text) => this.formatAmount(text),
        },
        {
          title: "Payment Terms",
          dataIndex: "paymentTerms",
          key: "paymentTerms",
        },
        {
          title: "Wallet Status",
          dataIndex: "walletStatus",
          key: "walletStatus",
          customRender: (text, record) => {
            const status = record.isWalletLinked ? "Active" : "Not Created";
            return {
              children: [
                this.$createElement(
                  "a-tag",
                  {
                    props: {
                      color: record.isWalletLinked ? "green" : "orange",
                    },
                  },
                  status
                ),
              ],
            };
          },
        },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          customRender: (text) => {
            return {
              children: [
                this.$createElement(
                  "a-tag",
                  {
                    props: {
                      color:
                        text === "Active"
                          ? "green"
                          : text === "Inactive"
                          ? "red"
                          : "orange",
                    },
                  },
                  text
                ),
              ],
            };
          },
        },
        {
          title: "Actions",
          key: "actions",
          scopedSlots: { customRender: "actions" },
        },
      ],
      keyAccountData: [],
      keyAccountLoading: false,
      statusOptions: ["Active", "Inactive", "On Hold", "Overdue"],
      paymentTermsOptions: [
        { label: "Next 30 days", value: 30 },
        { label: "Next 60 days", value: 60 },
        { label: "Next 90 days", value: 90 },
      ],
      segmentTypeOptions: [
        "Distributor",
        "Retailer",
        "Cooperative",
        "Agrovet",
        "Exclusive",
      ],
      stats: [
        {
          title: "Total Key Accounts",
          value: 0,
          icon: "fa fa-users",
          iconStyle: { color: "#2563eb" },
        },
        {
          title: "Exclusive Buyers",
          value: 0,
          icon: "fa fa-star",
          iconStyle: { color: "#f59e0b", fontSize: "14px" },
        },
        {
          title: "Total Credit Exposure",
          value: 0,
          icon: "fa fa-credit-card",
          iconStyle: { color: "#059669", fontSize: "14px" },
        },
        {
          title: "Overdue Invoices",
          value: 0,
          icon: "fa fa-exclamation-triangle",
          iconStyle: { color: "#ef4444", fontSize: "14px" },
        },
      ],
    };
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} key accounts`,
        showSizeChanger: false,
      };
    },
  },
  watch: {
    sellerId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchKeyAccounts();
        }
      },
    },
    'pagination.pageIndex'() {
      this.fetchKeyAccounts();
    },
    search() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.pageIndex = 1;
        this.fetchKeyAccounts();
      }, 400);
    },
    filters: {
      handler() {
        this.pagination.pageIndex = 1;
        this.fetchKeyAccounts();
      },
      deep: true,
    },
  },
  methods: {
    formatAmount,
    async fetchKeyAccounts() {
      this.keyAccountLoading = true;
      const response = await sellersService.getSellerKeyAccount(
        this.sellerId,
        {
          search: this.search,
          accountStatus: this.filters.accountStatus,
          segmentType: this.filters.segmentType,
          paymentTerms: this.filters.paymentTerms,
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
        }
      );
      if (response.data?.code === 200) {
        const data = response.data.data;
        this.keyAccountData = (data.results || []).map((account) => ({
          id: account.id,
          userSetupId: account.userSetupId,
          companyName: account.companyName || "N/A",
          segmentType: account.segmentType || "N/A",
          ownershipType: account.ownershipType || "N/A",
          numberOfEmployees: account.numberOfEmployees || 0,
          website: account.website || "N/A",
          customerSource: account.customerSource || "N/A",
          expectedRevenue: account.expectedRevenue || 0,
          distributionCenter: account.distributionCenter || "N/A",
          accountManager: account.accountManager || "N/A",
          purchaseValue: account.purchaseValue || 0,
          status: account.status || "Active",
          paymentTerms: account.paymentTerms || "N/A",
          businessType: account.businessType || "N/A",
          primaryContact: account.primaryContact || null,
          address: account.address || null,
                     preferences: account.preferences || null,
           shippingInformation: account.shippingInformation || null,
           createdAt: account.createdAt,
           updatedAt: account.updatedAt,
           accountStatus: account.accountStatus,
           isWalletLinked: account.isWalletLinked || false,
        }));
        this.pagination.totalCount = data.totalCount || 0;
        this.pagination.pageIndex = data.pageIndex || this.pagination.pageIndex;
        // Update stats
        this.stats[0].value = data.totalCount || 0;
        this.stats[1].value = this.keyAccountData.filter(
          (account) => account.segmentType === "Exclusive"
        ).length;
        this.stats[2].value = this.keyAccountData.reduce(
          (sum, account) => sum + (account.purchaseValue || 0),
          0
        );
        this.stats[3].value = this.keyAccountData.filter(
          (account) => account.status === "Overdue"
        ).length;
      } else {
        this.keyAccountData = [];
        this.stats.forEach((s) => (s.value = 0));
        this.pagination.totalCount = 0;
        this.$message.warning("An error occurred while fetching data")
      }
      this.keyAccountLoading = false;
    },
    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },
    viewKeyAccount(record) {
      this.$router.push({
        path: `/sellers/${this.sellerId}/key-accounts/${record.id}`,
      });
    },
  },
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
